import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ChevronDown, 
  Search, 
  User, 
  ShoppingBag, 
  Heart, 
  MapPin, 
  Info, 
  CreditCard, 
  LogOut,
  Clock,
  Star,
  ShoppingCart,
  Check,
  Home,
  Briefcase,
  Building,
  Navigation,
  Loader2,
  FileText,
  HelpCircle,
  Package,
  Trash2,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";
import SuspensionBanner from "../components/SuspensionBanner";

const API = `${getBackendUrl()}/api`;

// Categories data
const categories = [
  { name: "Burger", icon: "🍔" },
  { name: "South Indian", icon: "🥘" },
  { name: "North Indian", icon: "🍛" },
  { name: "Chinese", icon: "🥡" },
  { name: "Pizza", icon: "🍕" },
  { name: "Rolls", icon: "🌯" },
  { name: "Sweets", icon: "🍰" }
];

// Serviceable pincode for Fleety Food
const SERVICEABLE_PINCODE = "560075";

const HomePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showMyFleety, setShowMyFleety] = useState(false);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountSuccess, setDeleteAccountSuccess] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [fleetyPauseStatus, setFleetyPauseStatus] = useState({ orders_accepted: true, message: null, resume_at: null });
  const [fleetyGoPauseStatus, setFleetyGoPauseStatus] = useState({ orders_accepted: true, message: null, resume_at: null });
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState(null);
  
  // Check if current address is serviceable
  // If pincode is empty or not set, assume serviceable (user might not have set address yet)
  const isServiceable = () => {
    const pincode = selectedAddress?.pincode || user?.pincode;
    // If no pincode is set, or pincode is the serviceable one, return true
    if (!pincode || pincode === "" || pincode === SERVICEABLE_PINCODE) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // Check user suspension status from backend on every app load
    const checkUserStatus = async () => {
      try {
        const response = await axios.get(`${API}/user/status/${userData.id}`);
        if (response.data.is_suspended) {
          setIsSuspended(true);
          setSuspensionMessage(response.data.message);
          // Update localStorage with latest status
          localStorage.setItem("fleety_user", JSON.stringify({
            ...userData,
            status: response.data.status,
            is_suspended: response.data.is_suspended
          }));
        } else {
          setIsSuspended(false);
          setSuspensionMessage(null);
        }
      } catch (error) {
        console.error("Error checking user status:", error);
        // Check from stored user data as fallback
        if (userData.is_suspended || userData.status === "suspended") {
          setIsSuspended(true);
        }
      }
    };
    
    checkUserStatus();
    
    // Load selected delivery address from localStorage
    const savedAddress = localStorage.getItem("fleety_delivery_address");
    if (savedAddress) {
      setSelectedAddress(JSON.parse(savedAddress));
    }
  }, [navigate]);

  // Fetch service pause statuses on mount and poll every 2 minutes
  useEffect(() => {
    const fetchServiceStatuses = async () => {
      try {
        const response = await axios.get(`${API}/platform/order-status`);
        if (response.data.fleety) {
          setFleetyPauseStatus(response.data.fleety);
        }
        if (response.data.fleetygo) {
          setFleetyGoPauseStatus(response.data.fleetygo);
        }
      } catch (error) {
        console.error("Error fetching service statuses:", error);
        // Default to accepting orders on error
        setFleetyPauseStatus({ orders_accepted: true, message: null, resume_at: null });
        setFleetyGoPauseStatus({ orders_accepted: true, message: null, resume_at: null });
      }
    };

    fetchServiceStatuses();
    
    // Poll every 2 minutes (120000ms)
    const interval = setInterval(fetchServiceStatuses, 120000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (user) {
      fetchCart();
      fetchAddresses();
    }
  }, [user]);

  // Fetch restaurants when user or selected address changes
  useEffect(() => {
    if (user) {
      fetchRestaurants();
    }
  }, [user, selectedAddress]);

  const fetchRestaurants = async () => {
    try {
      let params = {};
      
      // If we have current GPS location, use lat/lng
      if (currentLocation) {
        params = { 
          lat: currentLocation.lat, 
          lng: currentLocation.lng 
        };
      } else {
        // Otherwise use pincode from selected address
        params = { 
          pincode: selectedAddress?.pincode || user?.pincode || "560075" 
        };
      }
      
      const response = await axios.get(`${API}/restaurants`, { params });
      // Ensure we always set an array
      setRestaurants(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };

  // Get current location using browser Geolocation API
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };
        setCurrentLocation(location);
        
        // Set as current delivery location
        const locationAddress = {
          type: "gps",
          label: "Current Location",
          address_line1: `GPS: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          address_line2: "",
          address_line3: "",
          pincode: "560075", // Default pincode for Bengaluru
          lat: latitude,
          lng: longitude
        };
        
        setSelectedAddress(locationAddress);
        localStorage.setItem("fleety_delivery_address", JSON.stringify(locationAddress));
        localStorage.setItem("fleety_current_location", JSON.stringify(location));
        
        setGettingLocation(false);
        setShowAddressDropdown(false);
        
        // Refresh restaurants with new location
        fetchRestaurantsWithLocation(location);
      },
      (error) => {
        setGettingLocation(false);
        console.error("Error getting location:", error);
        alert("Unable to get your location. Please check your browser permissions.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const fetchRestaurantsWithLocation = async (location) => {
    try {
      const response = await axios.get(`${API}/restaurants`, {
        params: { lat: location.lat, lng: location.lng }
      });
      setRestaurants(response.data);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${user.id}`);
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses/${user.id}`);
      setAddresses(response.data);
      
      // If no selected address, set the default one
      if (!selectedAddress) {
        const defaultAddr = response.data.find(a => a.is_default);
        if (defaultAddr) {
          setSelectedAddress({ type: "saved", ...defaultAddr });
          localStorage.setItem("fleety_delivery_address", JSON.stringify({ type: "saved", ...defaultAddr }));
        } else {
          // Use profile address as default
          const profileAddr = {
            type: "profile",
            label: user.label || "Home",
            address_line1: user.address_line1,
            address_line2: user.address_line2,
            address_line3: user.address_line3,
            pincode: user.pincode
          };
          setSelectedAddress(profileAddr);
          localStorage.setItem("fleety_delivery_address", JSON.stringify(profileAddr));
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMyFleety && !event.target.closest('[data-testid="my-fleety-menu"]')) {
        setShowMyFleety(false);
      }
      if (showAddressDropdown && !event.target.closest('[data-testid="address-dropdown"]')) {
        setShowAddressDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMyFleety, showAddressDropdown]);

  const handleLogout = () => {
    localStorage.removeItem("fleety_user");
    localStorage.removeItem("fleety_delivery_address");
    navigate("/");
  };

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    localStorage.setItem("fleety_delivery_address", JSON.stringify(address));
    setShowAddressDropdown(false);
  };

  const getAddressDisplay = () => {
    if (!selectedAddress) {
      if (!user) return "";
      return `${user.label || "Home"} - ${user.address_line1?.substring(0, 15)}... - ${user.pincode}`;
    }
    
    // Handle GPS location
    if (selectedAddress.type === "gps") {
      return "📍 Current Location";
    }
    
    const label = selectedAddress.label || "Home";
    const address = selectedAddress.address_line1 || "";
    const pincode = selectedAddress.pincode || "";
    return `${label} - ${address.substring(0, 15)}${address.length > 15 ? '...' : ''} - ${pincode}`;
  };

  const getLabelIcon = (label) => {
    if (!label) return Home;
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes("home")) return Home;
    if (lowerLabel.includes("office") || lowerLabel.includes("work")) return Briefcase;
    if (lowerLabel.includes("current") || lowerLabel.includes("gps")) return Navigation;
    return Building;
  };

  const cartCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Filter restaurants by search query and selected category
  const filteredRestaurants = (Array.isArray(restaurants) ? restaurants : []).filter(r => {
    const query = searchQuery.toLowerCase();
    
    // Match by restaurant name
    const matchesName = r.name.toLowerCase().includes(query);
    
    // Match by cuisine
    const matchesCuisine = r.cuisine.toLowerCase().includes(query);
    
    // Match by menu items
    const matchesMenuItem = r.menu?.some(item => 
      item.name.toLowerCase().includes(query)
    );
    
    const matchesSearch = searchQuery === "" || matchesName || matchesCuisine || matchesMenuItem;
    
    const matchesCategory = !selectedCategory || 
      r.cuisine.toLowerCase().includes(selectedCategory.toLowerCase());
    
    return matchesSearch && matchesCategory;
  });

  // Handle category click
  const handleCategoryClick = (categoryName) => {
    if (selectedCategory === categoryName) {
      setSelectedCategory(null); // Deselect if already selected
    } else {
      setSelectedCategory(categoryName);
    }
  };

  if (!user) {
    return null;
  }

  // Combine profile address with saved addresses for dropdown
  const allAddresses = [
    {
      type: "profile",
      label: user.label || "Home",
      address_line1: user.address_line1,
      address_line2: user.address_line2,
      address_line3: user.address_line3,
      pincode: user.pincode,
      isProfile: true
    },
    ...addresses.map(a => ({ ...a, type: "saved" }))
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="home-page">
      {/* Suspension Banner - shown when account is suspended */}
      {isSuspended && <SuspensionBanner message={suspensionMessage} />}
      
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-[#3FA9F5]"></div>
      
      {/* Header - Two Row Layout */}
      <header className="bg-[#3FA9F5] sticky top-0 z-40">
        {/* Row 1: Logo Centered */}
        <div className="flex items-center justify-center py-3 border-b border-white/10">
          <img 
            src="https://customer-assets.emergentagent.com/job_hungry-fleety/artifacts/oq66dorz_Fleety%20Logo%20zoomed.jpg" 
            alt="Fleety Logo" 
            className="h-10 w-auto"
            data-testid="header-logo"
          />
        </div>
        
        {/* Row 2: Delivering to | Cart | My Fleety */}
        <div className="flex items-center justify-between px-4 py-2">
          {/* Delivering to - with Dropdown */}
          <div className="relative flex-1" data-testid="address-dropdown">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowAddressDropdown(!showAddressDropdown);
                setShowMyFleety(false);
              }}
              className="flex items-center gap-1 text-white text-sm"
              data-testid="delivery-address-btn"
            >
              <MapPin size={16} className="text-white/80" />
              <span className="max-w-[140px] truncate">{getAddressDisplay()}</span>
              <ChevronDown size={14} className={`transition-transform ${showAddressDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Address Dropdown */}
            {showAddressDropdown && (
              <div className="absolute top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 animate-slideUp z-50 left-0" data-testid="address-dropdown-menu">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Select Delivery Address</p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {allAddresses.map((address, index) => {
                    const IconComponent = getLabelIcon(address.label);
                    const isSelected = selectedAddress && 
                      (address.type === selectedAddress.type) &&
                      (address.type === "profile" || address.id === selectedAddress.id);
                    
                    return (
                      <button
                        key={address.type === "profile" ? "profile" : address.id}
                        onClick={() => handleSelectAddress(address)}
                        className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left ${isSelected ? 'bg-[#3FA9F5]/5' : ''}`}
                        data-testid={`address-option-${index}`}
                      >
                        <div className={`w-8 h-8 ${isSelected ? 'bg-[#3FA9F5]/10' : 'bg-gray-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <IconComponent size={16} className={isSelected ? 'text-[#3FA9F5]' : 'text-gray-500'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm">{address.label}</span>
                            {address.isProfile && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Profile</span>
                            )}
                            {address.is_default && (
                              <span className="text-xs bg-[#3FA9F5] text-white px-1.5 py-0.5 rounded">Default</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {address.address_line1}
                            {address.address_line2 && `, ${address.address_line2}`}
                          </p>
                          <p className="text-xs text-gray-400">{address.pincode}</p>
                        </div>
                        {isSelected && (
                          <Check size={18} className="text-[#3FA9F5] flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {/* Use Current Location Button */}
                <div className="px-4 py-2 border-t border-gray-100">
                  <button 
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    className="w-full flex items-center justify-center gap-2 bg-[#3FA9F5] text-white text-sm font-medium py-3 rounded-lg hover:bg-[#2196F3] transition-colors disabled:opacity-50"
                  >
                    {gettingLocation ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Getting Location...</span>
                      </>
                    ) : (
                      <>
                        <Navigation size={16} />
                        <span>Use Current Location</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="px-4 py-2 border-t border-gray-100">
                  <button 
                    onClick={() => { setShowAddressDropdown(false); navigate("/addresses"); }}
                    className="w-full text-center text-[#3FA9F5] text-sm font-medium py-2 hover:underline"
                  >
                    + Add New Address
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Cart Icon */}
          <button
            onClick={() => navigate("/cart")}
            className="relative px-3 py-1"
            data-testid="my-cart-btn"
          >
            <ShoppingCart size={22} className="text-white" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-white text-[#3FA9F5] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>

          {/* My Fleety */}
          <div className="relative" data-testid="my-fleety-menu">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowMyFleety(!showMyFleety);
                setShowAddressDropdown(false);
              }}
              className="flex items-center gap-1 text-white text-sm font-medium"
              data-testid="my-fleety-btn"
            >
              <span>My Fleety</span>
              <ChevronDown size={14} className={`transition-transform ${showMyFleety ? 'rotate-180' : ''}`} />
            </button>

            {showMyFleety && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 animate-slideUp z-50 max-h-[70vh] overflow-y-auto" data-testid="my-fleety-dropdown">
                <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-white">
                  <p className="font-semibold text-gray-900">{user.full_name}</p>
                  <p className="text-sm text-gray-500">{user.phone_number}</p>
                </div>

                <div className="py-2">
                  {/* FleetyGo - Package Delivery */}
                  <button onClick={() => { setShowMyFleety(false); navigate("/fleetygo"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[#3FA9F5]/5 transition-colors text-left bg-gradient-to-r from-[#3FA9F5]/10 to-white" data-testid="menu-fleetygo">
                    <Package size={18} className="text-[#3FA9F5]" />
                    <span className="text-[#3FA9F5] font-semibold">FleetyGo</span>
                    <span className="ml-auto text-xs bg-[#3FA9F5] text-white px-2 py-0.5 rounded-full">Send Packages</span>
                  </button>
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  
                  <button onClick={() => { setShowMyFleety(false); navigate("/profile"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-profile">
                    <User size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">Profile</span>
                    <span className="ml-auto text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">Gift deals</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/orders"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-past-orders">
                    <ShoppingBag size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">My Orders</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/favourite-dishes"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-fav-dishes">
                    <Heart size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">Favourite dishes</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/favourite-restaurants"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-fav-restaurants">
                    <Star size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">Favourite restaurants</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/addresses"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-addresses">
                    <MapPin size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">Addresses</span>
                  </button>
                  
                  <div className="border-t border-gray-100 my-2"></div>
                  
                  <button onClick={() => { setShowMyFleety(false); navigate("/about"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-about">
                    <Info size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">About Fleety</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/terms"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-terms">
                    <FileText size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">Terms & Conditions</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/faqs"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-faqs">
                    <HelpCircle size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">FAQs</span>
                  </button>
                  <button onClick={() => { setShowMyFleety(false); navigate("/payment-methods"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left" data-testid="menu-payment">
                    <CreditCard size={18} className="text-[#3FA9F5]" />
                    <span className="text-gray-700">Payment methods</span>
                  </button>
                  
                  <div className="border-t border-gray-100 my-2"></div>
                  
                  <button 
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-red-50 transition-colors text-left text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                  
                  <button 
                    onClick={() => {
                      setShowMyFleety(false);
                      setShowDeleteAccountModal(true);
                    }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-red-50 transition-colors text-left text-red-500"
                    data-testid="menu-delete-account"
                  >
                    <Trash2 size={18} />
                    <span>Delete my account</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-[#3FA9F5]/10 to-white py-6 sm:py-8 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-testid="tagline">
              Fleety
            </h1>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base" data-testid="subtitle">
              No hidden fees. No inflated prices. Just food.
            </p>

            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={20} />
              <Input
                type="text"
                placeholder="Search for food or restaurants"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 sm:h-14 pl-12 pr-4 text-base sm:text-lg rounded-full border-2 border-gray-200 focus:border-[#3FA9F5] shadow-sm"
                data-testid="search-input"
              />
              
              {/* Search Results Dropdown */}
              {searchQuery.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 max-h-80 overflow-y-auto z-50 text-left">
                  {/* Matching Dishes */}
                  {restaurants.flatMap(r => 
                    (r.menu || [])
                      .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(item => ({ ...item, restaurant: r }))
                  ).slice(0, 5).length > 0 && (
                    <div className="p-2">
                      <p className="text-xs text-gray-500 px-3 py-1 font-medium">Dishes</p>
                      {restaurants.flatMap(r => 
                        (r.menu || [])
                          .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
                          .map(item => ({ ...item, restaurant: r }))
                      ).slice(0, 5).map((item, idx) => (
                        <button
                          key={`dish-${idx}`}
                          onClick={() => {
                            setSearchQuery("");
                            navigate(`/restaurant/${item.restaurant.id}`);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-lg">🍽️</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{item.name}</p>
                            <p className="text-xs text-gray-500 truncate">{item.restaurant.name}</p>
                          </div>
                          <span className="text-sm font-semibold text-[#3FA9F5]">₹{item.price}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Matching Restaurants */}
                  {restaurants.filter(r => 
                    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    r.cuisine.toLowerCase().includes(searchQuery.toLowerCase())
                  ).slice(0, 3).length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500 px-3 py-1 font-medium">Restaurants</p>
                      {restaurants.filter(r => 
                        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        r.cuisine.toLowerCase().includes(searchQuery.toLowerCase())
                      ).slice(0, 3).map((r, idx) => (
                        <button
                          key={`rest-${idx}`}
                          onClick={() => {
                            setSearchQuery("");
                            navigate(`/restaurant/${r.id}`);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          {r.image_url ? (
                            <img src={r.image_url} alt={r.name} className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-lg">🏪</div>
                          )}
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-medium text-gray-900 text-sm truncate">{r.name}</p>
                            <p className="text-xs text-gray-500 truncate">{r.cuisine}</p>
                          </div>
                          <div className="flex items-center gap-1 text-sm">
                            <Star size={12} className="text-yellow-500 fill-yellow-500" />
                            <span className="font-medium">{r.rating}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No Results */}
                  {restaurants.flatMap(r => 
                    (r.menu || []).filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  ).length === 0 && 
                  restaurants.filter(r => 
                    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    r.cuisine.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No dishes or restaurants found for &quot;{searchQuery}&quot;
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Service Pause Banners - Show when services are not accepting orders */}
        {(!fleetyPauseStatus.orders_accepted || !fleetyGoPauseStatus.orders_accepted) && (
          <div className="px-4 pb-2 space-y-2">
            <div className="max-w-6xl mx-auto space-y-2">
              {/* Fleety (Food) Pause Banner - Orange */}
              {!fleetyPauseStatus.orders_accepted && (
                <div 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 shadow-lg"
                  data-testid="fleety-pause-banner"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">🍔</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm sm:text-base leading-relaxed">
                        {fleetyPauseStatus.message || "We are currently not accepting food orders. Please try again later."}
                      </p>
                      <p className="text-white/80 text-xs sm:text-sm mt-1">
                        We will notify you when food orders resume.
                      </p>
                      {fleetyPauseStatus.resume_at && (
                        <p className="text-white/90 text-xs sm:text-sm mt-2 font-medium">
                          ⏰ Expected: {new Date(fleetyPauseStatus.resume_at).toLocaleString('en-IN', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            day: 'numeric',
                            month: 'short'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* FleetyGo (Package) Pause Banner - Purple */}
              {!fleetyGoPauseStatus.orders_accepted && (
                <div 
                  className="bg-gradient-to-r from-purple-500 to-violet-500 rounded-xl p-4 shadow-lg"
                  data-testid="fleetygo-pause-banner"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">📦</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm sm:text-base leading-relaxed">
                        {fleetyGoPauseStatus.message || "FleetyGo is currently not accepting deliveries. We will be back soon!"}
                      </p>
                      <p className="text-white/80 text-xs sm:text-sm mt-1">
                        We will notify you when package deliveries resume.
                      </p>
                      {fleetyGoPauseStatus.resume_at && (
                        <p className="text-white/90 text-xs sm:text-sm mt-2 font-medium">
                          ⏰ Expected: {new Date(fleetyGoPauseStatus.resume_at).toLocaleString('en-IN', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            day: 'numeric',
                            month: 'short'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Categories Section */}
        <div className="px-4 py-4 sm:py-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4" data-testid="categories-title">
              Categories
            </h2>
            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(category.name)}
                  className={`flex-shrink-0 flex flex-col items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 transition-all min-w-[70px] sm:min-w-[90px] ${
                    selectedCategory === category.name 
                      ? 'bg-[#3FA9F5] border-[#3FA9F5] text-white' 
                      : 'bg-white border-gray-100 hover:border-[#3FA9F5] hover:bg-[#3FA9F5]/5'
                  }`}
                  data-testid={`category-${category.name.toLowerCase().replace(' ', '-')}`}
                >
                  <span className="text-xl sm:text-2xl">{category.icon}</span>
                  <span className={`text-xs sm:text-sm font-medium ${selectedCategory === category.name ? 'text-white' : 'text-gray-700'}`}>{category.name}</span>
                </button>
              ))}
            </div>
            {selectedCategory && (
              <button 
                onClick={() => setSelectedCategory(null)}
                className="mt-2 text-sm text-[#3FA9F5] hover:underline"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>

        {/* Restaurants Section */}
        <div className="px-4 py-4 sm:py-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4" data-testid="restaurants-title">
              Restaurants near you
            </h2>
            
            {/* Launching Soon Banner - Show when not serviceable */}
            {!isServiceable() && (
              <div className="mb-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-4 sm:p-6 text-white shadow-lg" data-testid="launching-soon-banner">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🚀</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg sm:text-xl mb-1">Launching Soon in Your Area!</h3>
                    <p className="text-white/90 text-sm sm:text-base mb-3">
                      Fleety Food is currently available only in pincode <span className="font-bold">560075</span>. 
                      We&apos;re expanding soon!
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => navigate("/fleetygo")}
                        className="bg-white text-orange-600 px-4 py-2 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors"
                      >
                        Try FleetyGo Instead →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
              </div>
            ) : (
              <div className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 ${!isServiceable() ? 'opacity-50 pointer-events-none' : ''}`}>
                {filteredRestaurants.map((restaurant) => (
                  <div
                    key={restaurant.id}
                    onClick={() => isServiceable() && navigate(`/restaurant/${restaurant.id}`)}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    data-testid={`restaurant-${restaurant.id}`}
                  >
                    <div className="relative h-28 sm:h-40">
                      <img
                        src={restaurant.image_url || restaurant.image}
                        alt={restaurant.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 bg-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg flex items-center gap-1 text-xs sm:text-sm font-medium">
                        <Star size={12} className="text-yellow-500 fill-yellow-500" />
                        <span>{restaurant.rating}</span>
                      </div>
                    </div>
                    <div className="p-2 sm:p-4">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{restaurant.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{restaurant.cuisine}</p>
                      <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          <span>{restaurant.delivery_time}</span>
                        </div>
                        <span>•</span>
                        <span>{restaurant.distance} km</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Cart Floating Button */}
      {cartCount > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-50">
          <Button
            onClick={() => navigate("/cart")}
            className="w-full sm:w-auto h-14 px-6 bg-[#3FA9F5] hover:bg-[#2196F3] text-white font-semibold rounded-full shadow-lg flex items-center justify-center gap-2"
            data-testid="floating-cart-btn"
          >
            <ShoppingCart size={20} />
            <span>{cartCount} items</span>
            <span>•</span>
            <span>₹{cart.subtotal || 0}</span>
          </Button>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 sm:py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 text-xs sm:text-sm">© 2025 Fleety. All rights reserved.</p>
          <p className="text-gray-500 text-xs mt-2">No hidden fees. No inflated prices. Just food.</p>
        </div>
      </footer>

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden animate-slideUp">
            {!deleteAccountSuccess ? (
              <>
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle size={32} className="text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Account?</h2>
                  <p className="text-gray-600 text-sm">
                    Please note this change cannot be undone. Are you sure you want to delete your account?
                  </p>
                </div>
                <div className="flex border-t border-gray-100">
                  <button
                    onClick={() => setShowDeleteAccountModal(false)}
                    className="flex-1 py-4 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                    data-testid="delete-account-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setDeletingAccount(true);
                      try {
                        const response = await axios.post(`${API}/delete-account-request`, {
                          user_id: user.id,
                          phone_number: user.phone_number,
                          reason: "User requested account deletion"
                        });
                        if (response.data.success) {
                          setDeleteAccountSuccess(true);
                        }
                      } catch (err) {
                        console.error("Error submitting deletion request:", err);
                        alert("Something went wrong. Please try again.");
                      } finally {
                        setDeletingAccount(false);
                      }
                    }}
                    disabled={deletingAccount}
                    className="flex-1 py-4 text-red-600 font-medium hover:bg-red-50 transition-colors border-l border-gray-100 disabled:opacity-50"
                    data-testid="delete-account-confirm"
                  >
                    {deletingAccount ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Yes, Delete"}
                  </button>
                </div>
              </>
            ) : (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Request Received</h2>
                <p className="text-gray-600 text-sm mb-6">
                  We have received your request. We will delete your account and confirm within 5-7 business days.
                </p>
                <Button
                  onClick={() => {
                    setShowDeleteAccountModal(false);
                    setDeleteAccountSuccess(false);
                  }}
                  className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white font-medium rounded-full"
                  data-testid="delete-account-ok"
                >
                  OK
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
